'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Award, BookOpen, ChevronDown, Compass, HelpCircle, LogOut, Settings, Shield } from 'lucide-react'
import { FaDiscord } from 'react-icons/fa'
import { cn, Skeleton } from '@sharpr/ui'
import { DEFAULT_SPORT } from '@sharpr/types'
import { useAccess } from '@/hooks/useAccess'
import { useIsReferrer } from '@/hooks/useIsReferrer'
import { useSession } from '@/hooks/useSession'
import { useDiscordStatus } from '@/hooks/useDiscordStatus'
import { useAccessState } from '@/hooks/useAccessState'
import { createClient } from '@/lib/supabase/client'
import { useOptionalTour } from '@/components/client/tour/TourContext'
import { buildSharprTourSteps, tourSportFromPath } from '@/components/client/tour/tourSteps'
import { captureEvent, EVENTS } from '@/lib/posthog/events'
import { signInOrOutHere } from '@/lib/auth/thisTab'

const MENU_ITEM_CLASS =
  'w-full flex items-center gap-3 px-4 py-2 text-sm text-fg dark:text-text-primary hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200'

export function UserMenu(): React.JSX.Element | null {
  const { user, loading } = useSession()
  const access = useAccess()
  const { isReferrer } = useIsReferrer()
  const { linked: discordLinked } = useDiscordStatus()
  const { hasAccess } = useAccessState()
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const supabase = useMemo(() => createClient(), [])

  // Walkthrough: while the tour is on the `settings-nav` step, force the
  // dropdown open so the spotlight can land on the Settings link inside, and
  // suppress click-outside dismissal so the spotlight backdrop doesn't slam
  // the menu shut.
  const tour = useOptionalTour()
  const tourWantsMenuOpen = tour?.active === true && tour.step?.id === 'settings-nav'
  useEffect(() => {
    if (tour?.active) setIsOpen(tourWantsMenuOpen)
  }, [tour?.active, tourWantsMenuOpen])

  const getMenuPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return { top: 0, right: 0 }
    return { top: rect.bottom + 8, right: window.innerWidth - rect.right }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (tourWantsMenuOpen) return

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, tourWantsMenuOpen])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  async function handleSignOut(): Promise<void> {
    try {
      const { error } = await signInOrOutHere(() => supabase.auth.signOut())
      if (error) console.error('[UserMenu] Sign out error:', error)
    } catch (err) {
      console.error('[UserMenu] Sign out failed:', err)
    } finally {
      window.location.href = '/login'
    }
  }

  // HEIGHT CONTRACT: AppHeader pins its auth slot to h-10 sm:h-12 (40/48px) —
  // the tallest of this component's three states (40px skeleton, ~33px anon
  // buttons, py-1.5+h-7 / py-2+h-8 = 40/48px avatar button) — so the
  // skeleton→anon/avatar swap is height-neutral (the p75 0.26 mobile CLS fix).
  // If any state here grows taller, update the slot in AppHeader.tsx.
  if (loading) {
    return <Skeleton className="w-10 h-10 rounded-full" />
  }

  if (!user) {
    // Anonymous viewers can now reach the odds pages. Sign in preserves the
    // current page (resume intent — existing user wants to continue what they
    // were doing). Start trial is a conversion CTA — it always funnels through
    // /signup → /pricing (SignUpForm's default), never returns to the current
    // page, so a logged-out browser on / doesn't get bounced back to /.
    const signInRedirect = encodeURIComponent(pathname || `/${DEFAULT_SPORT}`)
    return (
      <div data-tour="tour-demo-account-area" className="flex items-center gap-2">
        <Link
          href={`/login?redirect=${signInRedirect}`}
          onClick={() =>
            captureEvent(EVENTS.CTA_CLICKED, { location: 'header_sign_in', label: 'Sign in', destination: '/login' })
          }
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-fg dark:text-text-primary bg-white dark:bg-bg-secondary border border-border dark:border-border-secondary rounded-md hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200 shadow-sm"
        >
          Sign in
        </Link>
        <Link
          href="/signup"
          onClick={() =>
            captureEvent(EVENTS.CTA_CLICKED, { location: 'header_start_trial', label: 'Start trial', destination: '/signup' })
          }
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all duration-200 shadow-sm whitespace-nowrap"
        >
          Start trial
        </Link>
      </div>
    )
  }

  const userEmail = user.email ?? 'User'
  const displayName = user.user_metadata?.full_name as string | undefined
  const initials = displayName
    ? displayName.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase()
    : userEmail.substring(0, 2).toUpperCase()

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white text-xs sm:text-sm font-medium">
          {user.user_metadata?.avatar_url ? (
            <Image
              src={user.user_metadata.avatar_url}
              alt={userEmail}
              width={32}
              height={32}
              className="rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>

        <span className="hidden md:block text-sm font-medium text-fg dark:text-text-primary max-w-[150px] truncate">
          {displayName || userEmail}
        </span>

        <ChevronDown
          className={cn(
            'w-4 h-4 text-muted-foreground dark:text-text-tertiary transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-56 bg-white dark:bg-bg-secondary border border-border dark:border-border-secondary rounded-lg shadow-lg py-1 z-50"
          style={getMenuPosition()}
        >
          <div className="px-4 py-3 border-b border-border dark:border-border-secondary">
            <p className="text-sm font-medium text-fg dark:text-text-primary truncate">
              {user.user_metadata?.full_name || userEmail}
            </p>
            <p className="text-xs text-muted-foreground dark:text-text-tertiary mt-0.5 truncate">
              {user.user_metadata?.full_name ? userEmail : (isReferrer ? 'Ambassador' : '')}
            </p>
          </div>

          <div className="py-1">
            {tour && (
              <button
                type="button"
                onClick={() => {
                  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                  const sport = tourSportFromPath(pathname);
                  setIsOpen(false);
                  tour.start(buildSharprTourSteps({ sport, isMobile, hasAccess: access.tier(sport) !== 'public' }));
                }}
                className={MENU_ITEM_CLASS}
              >
                <Compass className="w-4 h-4" />
                Take the tour
              </button>
            )}

            <Link href="/settings" data-tour="tour-demo-settings-link" className={MENU_ITEM_CLASS}>
              <Settings className="w-4 h-4" />
              Settings
            </Link>

            <Link href="/how-to-use" className={MENU_ITEM_CLASS}>
              <BookOpen className="w-4 h-4" />
              How to use
            </Link>

            <Link href="/faq" className={MENU_ITEM_CLASS}>
              <HelpCircle className="w-4 h-4" />
              FAQ
            </Link>

            {isReferrer && (
              <Link href="/ambassador" className={MENU_ITEM_CLASS}>
                <Award className="w-4 h-4" />
                Ambassador
              </Link>
            )}

            {access.isAdmin && (
              <Link href="/admin" className={MENU_ITEM_CLASS}>
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}

            {!discordLinked && !access.isAdmin && hasAccess && (
              <a href="/api/auth/discord" className={MENU_ITEM_CLASS}>
                <FaDiscord className="w-4 h-4" />
                Join Discord
              </a>
            )}

            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
